import {
  useCallback,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  MemberListHeader,
  MemberListItem,
  MemberSearchBox,
  MemberTable,
  MemberTableHeader,
  MemberTableInfo,
} from "../style";
import { PaginationWrapper } from "../../../component/CommonTable";
import {
  getMemberDashboardListSort,
  getMemberSurveyList,
  SellerMemberDto,
  SellerMemberResponse,
} from "../../../api/member";
import PopupPagination from "../../../component/Pagination/popupPagination";
import { useRecoilValue } from "recoil";
import { pubState } from "../../../recoil/pub.atom";
import { Title } from "../../../styles/page";
import DropBox from "../../../component/DropBox";
import * as ExcelJS from "exceljs";
import { exchangePoint } from "../../../utils/number";

interface MemberTableProps {
  sort: string;
  setSort: (value: string) => void;
  order: string;
  setOrder: (value: string) => void;
  setMember: (value: SellerMemberDto | null) => void;
}

export interface MemberTableRef {
  getList: () => void;
}

const MemberTableComponent = forwardRef<MemberTableRef, MemberTableProps>(
  ({ sort, setSort, order, setOrder, setMember }, ref) => {
    const [list, setList] = useState<SellerMemberDto[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPage, setTotalPage] = useState(1);
    const [total, setTotal] = useState(1);
    const [isHover, setIsHover] = useState<boolean>(false);
    const [searchMember, setSearchMember] = useState<string>("");
    const pub = useRecoilValue(pubState);
    const [year, setYear] = useState<string>("");
    const [month, setMonth] = useState<string>("");

    const onPageChange = async (nextPage: number) => {
      setCurrentPage(nextPage);
    };

    const getList = useCallback(() => {
      if (!pub) return;
      try {
        getMemberDashboardListSort(
          currentPage,
          10,
          pub.id,
          searchMember,
          sort,
          order,
          year,
          month,
        ).then((res) => setData(res));
      } catch (e) {
        console.error(e);
      }
    }, [pub, currentPage, searchMember, sort, order, year, month]);

    // 외부에서 getList를 호출할 수 있도록 노출
    useImperativeHandle(ref, () => ({
      getList,
    }));

    const onSearchClick = () => {
      getList();
      onPageChange(1);
    };

    const setData = (res: SellerMemberResponse) => {
      setList(res.list);
      setTotalPage(Math.max(1, Math.ceil(res.total / 10)));
      setTotal(res.total);
    };

    useEffect(() => {
      getList();
    }, [getList]);

    useEffect(() => {
      setCurrentPage(1);
      if (year === "" && month !== "") setMonth("");
    }, [year, month]);

    const formatTime = (dateTime: string, time: boolean = false) => {
      if (!dateTime) return null;

      if (time) return dateTime.split(" ")[1];

      const date = dateTime.split("T")[0];
      const [yyyy, mm, dd] = date.split("-");

      return `${yyyy}/${mm}/${dd}`;
    };

    // YYYY-MM-DD HH:MM:SS
    const formatDate = (dateTime: string) => {
      if (!dateTime) return null;
      const date = dateTime.split("T");
      const [yyyy, MM, dd] = date[0].split("-");
      const [hh, mm] = date[1].split(":");
      return `${yyyy}-${MM}-${dd} ${hh}:${mm}`;
    };

    const handleMore = () => {
      setIsHover(true);
    };

    const handleMoreOut = () => {
      setIsHover(false);
    };

    const handleSort = (sortKey: string) => {
      if (sort === sortKey) {
        // 같은 컬럼 클릭 시 정렬 방향 토글
        setOrder(order === "DESC" ? "ASC" : "DESC");
      } else {
        // 다른 컬럼 클릭 시 해당 컬럼으로 정렬하고 DESC(높은순)로 시작
        setSort(sortKey);
        setOrder("DESC");
      }
    };

    const monthList = [
      { name: "월 선택", value: "" },
      { name: "1월", value: "1" },
      { name: "2월", value: "2" },
      { name: "3월", value: "3" },
      { name: "4월", value: "4" },
      { name: "5월", value: "5" },
      { name: "6월", value: "6" },
      { name: "7월", value: "7" },
      { name: "8월", value: "8" },
      { name: "9월", value: "9" },
      { name: "10월", value: "10" },
      { name: "11월", value: "11" },
      { name: "12월", value: "12" },
    ];

    const yearList = [
      { name: "연도 선택", value: "" },
      { name: "2025년", value: "2025" },
      { name: "2026년", value: "2026" },
    ];

    const downloadExcel = async (data: any[]) => {
      try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("회원 설문 조사");

        // 헤더 설정
        worksheet.columns = [
          { header: "날짜", key: "날짜", width: 20 },
          { header: "닉네임", key: "닉네임", width: 15 },
          { header: "방문경로", key: "방문경로", width: 15 },
          { header: "상세경로", key: "상세경로", width: 20 },
          { header: "거주지역", key: "거주지역", width: 15 },
          { header: "성별", key: "성별", width: 10 },
          { header: "연령대", key: "연령대", width: 12 },
          { header: "홀덤경력", key: "홀덤경력", width: 15 },
          {
            header: "즐겨보는 홀덤 컨텐츠",
            key: "즐겨보는 홀덤 컨텐츠",
            width: 20,
          },
          {
            header: "타 홀덤펍 이용 경험",
            key: "타 홀덤펍 이용 경험",
            width: 18,
          },
          { header: "비고", key: "비고", width: 15 },
        ];

        // 헤더 스타일 설정
        worksheet.getRow(1).font = { bold: true };
        worksheet.getRow(1).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE6E6FA" },
        };
        worksheet.getRow(1).alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        // 데이터 추가
        data.forEach((item) => {
          worksheet.addRow(item);
        });

        // 모든 셀 정렬
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber > 1) {
            row.eachCell((cell) => {
              cell.alignment = { vertical: "middle", horizontal: "center" };
            });
          }
        });

        // 엑셀 파일 생성 및 다운로드
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const dateStr = year && month ? `${year}년_${month}월` : "전체";
        link.download = `회원_설문조사_${pub?.pubName || "매장"}_${dateStr}_${
          new Date().toISOString().split("T")[0]
        }.xlsx`;
        link.click();

        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("엑셀 다운로드 실패:", error);
        alert("엑셀 다운로드에 실패했습니다.");
      }
    };

    const convertExperience = (experience: number) => {
      if (experience === 1) return "홀덤 초보";
      if (experience === 3) return "3개월 이상";
      if (experience === 12) return "1년 이상";
      if (experience === 36) return "3년 이상";
    };

    const getSurveyList = async () => {
      if (!pub) return;
      try {
        const res = await getMemberSurveyList(pub.id, year, month);

        if (res.length === 0) {
          alert("다운로드할 설문 조사 데이터가 없습니다.");
          return;
        }

        const excelData = res.map((item) => ({
          날짜: formatDate(item.createdAt.toString()),
          닉네임: item.nickname,
          방문경로: item.route,
          상세경로: "",
          거주지역: item.area,
          성별: item.gender === 0 ? "남" : "여",
          연령대: item.age,
          홀덤경력: convertExperience(item.experience),
          "즐겨보는 홀덤 컨텐츠": "",
          "타 홀덤펍 이용 경험": item.usedPub ? "O" : "X",
          비고: "",
        }));

        await downloadExcel(excelData);
      } catch (e) {
        console.error(e);
        alert("엑셀 다운로드 중 오류가 발생했습니다.");
      }
    };

    return (
      <MemberTable>
        <MemberListHeader>
          <Title weight={700} size={20}>
            매장 회원 리스트
          </Title>
          <div className="right">
            <MemberSearchBox>
              <input
                type="text"
                placeholder="닉네임을 검색하세요. "
                value={searchMember}
                onKeyUp={(e) => {
                  if (e.key === "Enter") {
                    onSearchClick();
                  }
                }}
                onChange={(e) => {
                  onPageChange(1);
                  setSearchMember(e.target.value);
                }}
              />
              <div className="searchIcon" onClick={onSearchClick}>
                <img
                  src="/image-web/timer-config/searchIcon.svg"
                  alt="검색 아이콘"
                />
              </div>
            </MemberSearchBox>
            <DropBox list={yearList} value={year} setValue={setYear} />
            <DropBox list={monthList} value={month} setValue={setMonth} />
            <div className="button" onClick={getSurveyList}>
              엑셀 다운로드
            </div>
          </div>
        </MemberListHeader>
        <MemberTableHeader>
          <div className="item z"></div>
          <div className="item a">회원 닉네임</div>
          <div className="item b sortable" onClick={() => handleSort("date")}>
            <span>가입날짜</span>
            <div className="sort-arrows">
              <img
                src="/image-web/edit/arrow_down.svg"
                alt="up"
                className={`arrow-up ${
                  sort === "date" && order === "ASC" ? "active" : ""
                }`}
              />
              <img
                src="/image-web/edit/arrow_down.svg"
                alt="down"
                className={`arrow-down ${
                  sort === "date" && order === "DESC" ? "active" : ""
                }`}
              />
            </div>
          </div>
          <div className="item e">
            참여 횟수
            <img
              onMouseOver={() => handleMore()}
              onMouseOut={() => handleMoreOut()}
              src="/image-web/solution/warning.png"
              alt="!"
            />
            {isHover && (
              <MemberTableInfo>
                * 애드온은 참여횟수에 집계되지 않습니다.{" "}
              </MemberTableInfo>
            )}
          </div>
          <div className="item g sortable" onClick={() => handleSort("point")}>
            <span>
              보유 <br />
              매장 이용권 (E)
            </span>
            <div className="sort-arrows">
              <img
                src="/image-web/edit/arrow_down.svg"
                alt="up"
                className={`arrow-up ${
                  sort === "point" && order === "ASC" ? "active" : ""
                }`}
              />
              <img
                src="/image-web/edit/arrow_down.svg"
                alt="down"
                className={`arrow-down ${
                  sort === "point" && order === "DESC" ? "active" : ""
                }`}
              />
            </div>
          </div>
          <div className="item c sortable" onClick={() => handleSort("seed")}>
            <span>보유 크레딧 (RABBIT)</span>
            <div className="sort-arrows">
              <img
                src="/image-web/edit/arrow_down.svg"
                alt="up"
                className={`arrow-up ${
                  sort === "seed" && order === "ASC" ? "active" : ""
                }`}
              />
              <img
                src="/image-web/edit/arrow_down.svg"
                alt="down"
                className={`arrow-down ${
                  sort === "seed" && order === "DESC" ? "active" : ""
                }`}
              />
            </div>
          </div>
          <div className="item c sortable" onClick={() => handleSort("carrot")}>
            <span>보유 캐럿 (CARROT)</span>
            <div className="sort-arrows">
              <img
                src="/image-web/edit/arrow_down.svg"
                alt="up"
                className={`arrow-up ${
                  sort === "carrot" && order === "ASC" ? "active" : ""
                }`}
              />
              <img
                src="/image-web/edit/arrow_down.svg"
                alt="down"
                className={`arrow-down ${
                  sort === "carrot" && order === "DESC" ? "active" : ""
                }`}
              />
            </div>
          </div>
          <div
            className="item c sortable"
            onClick={() => handleSort("mileage")}
          >
            <span>
              보유 <br />
              주류 마일리지
            </span>
            <div className="sort-arrows">
              <img
                src="/image-web/edit/arrow_down.svg"
                alt="up"
                className={`arrow-up ${
                  sort === "mileage" && order === "ASC" ? "active" : ""
                }`}
              />
              <img
                src="/image-web/edit/arrow_down.svg"
                alt="down"
                className={`arrow-down ${
                  sort === "mileage" && order === "DESC" ? "active" : ""
                }`}
              />
            </div>
          </div>
          <div className="item h">비고</div>
        </MemberTableHeader>
        <div className="list-wrapper">
          {list.map((item, index) => (
            <MemberListItem key={index} onClick={() => setMember(item)}>
              <div className="item z">
                {total - ((currentPage - 1) * 10 + index)}
              </div>
              <div className="item a">
                <span>{item.nickname}</span>
              </div>
              <div className="item b">{formatTime(item.createdAt)}</div>
              <div className="item e">{item.count}</div>
              <div className="item g">
                <div>{exchangePoint(item.point)}</div>
              </div>
              <div className="item c">{Number(item.seed).toLocaleString()}</div>
              <div className="item c">
                {Number(item.carrot ?? 0).toLocaleString()}
              </div>
              <div className="item c">
                {Number(item.mileage).toLocaleString()}
              </div>
              <div className="item h">
                {item.deletedAt || !item.birthDay ? "계정탈퇴" : "-"}
              </div>
            </MemberListItem>
          ))}
        </div>
        {totalPage > 1 && (
          <PaginationWrapper>
            <PopupPagination
              currentPage={currentPage}
              totalPages={totalPage}
              onPageChange={onPageChange}
            />
          </PaginationWrapper>
        )}
      </MemberTable>
    );
  },
);

MemberTableComponent.displayName = "MemberTableComponent";

export default MemberTableComponent;
